import React, { FunctionComponent } from 'react'
import * as S from './AdjustmentFactors.styles'
import ExpandableAdjustmentLabel from './ExapandableAdjustmentLabel'
import FactorButton from './FactorButton'
import { AdmGroupFactor } from '../types'
import PlaceHolderLabel from './PlaceHolderLabel'
import OrLabel from './OrLabel'
import { AdjustmentItem, EvaluationUserView } from '@app/evaluation-core/types'

type OwnProps = {
  totalValueSign: '' | '+'
  totalValue: string | number | undefined
  notOr: boolean
  factor: AdmGroupFactor
  lzAdjustments: number[]
  factorIndex: number
  index: number
  openFactors: string[]
  setOpenFactors: React.Dispatch<React.SetStateAction<string[]>>
  view: EvaluationUserView
  adjustments: AdjustmentItem[]
}

type Props = OwnProps

export const LzAndShotAdjustment: FunctionComponent<Props> = ({
  totalValueSign,
  totalValue,
  notOr,
  factor,
  lzAdjustments,
  factorIndex,
  index,
  openFactors,
  view,
  adjustments,
  setOpenFactors,
}) => {
  const handleShowOr = (factor: AdmGroupFactor) =>
    typeof factor.showOr === 'function'
      ? factor.showOr(adjustments, view)
      : factor.showOr

  const visibleItems = factor.items
    .map((button, buttonIndex) => ({ button, buttonIndex }))
    .filter(({ button }) => {
      if (typeof button.shouldRender === 'function') {
        return button.shouldRender(adjustments, view)
      }
      return button.shouldRender !== false
    })

  const maxLz = lzAdjustments.length
  const lzIndices = Array.from({ length: maxLz }, (_, i) => i)

  const showLeftColumn = factor.label && notOr
  const showOr = handleShowOr(factor)
  const rowSpan = Math.max(visibleItems.length, 1)
  const orRowSpan = Math.min(rowSpan, 2)
  
  const hasWideButtons = visibleItems.some(({ button }) => button.wideButton)

  return (
    <S.AdmFactorContainerV2 key={factorIndex}>
      <S.AdmFactorCardGridV2 
        lzCount={Math.max(maxLz, 1)} 
        showOr={!!showOr}
        hasWideButtons={hasWideButtons}
      >
        <S.AdmFactorLeftCellV2 style={{ gridRow: `1 / span ${rowSpan}` }}>
          {showLeftColumn && (
            <>
              <S.AdmFactorHeaderRowV2>
                <ExpandableAdjustmentLabel
                  factorIndex={factorIndex}
                  index={index}
                  openFactors={openFactors}
                  setOpenFactors={setOpenFactors}
                  factor={factor}
                />
              </S.AdmFactorHeaderRowV2>
              <S.AdmFactorTotalStackV2>
                <S.TotalValueV2>
                  {factor.totalKey ? `${totalValueSign}${totalValue}` : 0}{' '}
                </S.TotalValueV2>
                <S.TotalTextV2>Cumulative Total</S.TotalTextV2>
              </S.AdmFactorTotalStackV2>
            </>
          )}
        </S.AdmFactorLeftCellV2>

        <S.AdmFactorOrCellV2
          style={{ gridRow: `1 / span ${orRowSpan}`, height: '100%' }}
        >
          {showOr ? (
            <span>or</span>
          ) : (
            lzAdjustments?.length < 5 && <PlaceHolderLabel />
          )}
        </S.AdmFactorOrCellV2>

        {visibleItems.map(({ button, buttonIndex }, itemIndex) => (
          <React.Fragment key={`item-${itemIndex}`}>
            {lzIndices.map((shotIndex) => (
              <S.AdmFactorLzCellV2
                key={`lz-${shotIndex}-item-${itemIndex}`}
                style={{
                  gridColumn: 3 + shotIndex,
                  gridRow: itemIndex + 1,
                }}
              >
                {notOr && itemIndex === 0 && (
                  <S.AdmFactorLzHeaderCellV2>
                    {shotIndex + 1 === lzAdjustments?.length &&
                    factor.type !== 'landingZones' ? (
                      <S.ButtonHeader>{`Green`}</S.ButtonHeader>
                    ) : (
                      <S.ButtonHeader>{`LZ ${shotIndex + 1}`}</S.ButtonHeader>
                    )}
                  </S.AdmFactorLzHeaderCellV2>
                )}
                <FactorButton
                  buttonIndex={buttonIndex}
                  view={view}
                  adjustments={adjustments}
                  useLz
                  useShot
                  shotIndex={shotIndex}
                  factor={factor}
                  button={button}
                  hideText
                />
              </S.AdmFactorLzCellV2>
            ))}
            {!factor.factorText && (
              <S.AdmFactorTextCellV2
                style={{
                  gridColumn: 3 + Math.max(maxLz, 0),
                  gridRow: itemIndex + 1,
                }}
              >
                <S.AdmFactorTextV2>
                  {button.text && (
                    <S.AdmFactorTextLabel>{button.text}</S.AdmFactorTextLabel>
                  )}
                  {button.renderText && button.renderText(adjustments, view)}
                  {factor.selectWidget && factor.selectWidget()}
                </S.AdmFactorTextV2>
              </S.AdmFactorTextCellV2>
            )}
          </React.Fragment>
        ))}
        {factor.factorText && (
          <S.AdmFactorTextCellV2
            style={{
              gridColumn: 3 + Math.max(maxLz, 0),
              gridRow: `1 / span ${rowSpan}`,
            }}
          >
            <S.AdmFactorTextV2>
              <S.AdmFactorTextLabel>{factor.factorText}</S.AdmFactorTextLabel>
            </S.AdmFactorTextV2>
          </S.AdmFactorTextCellV2>
        )}
      </S.AdmFactorCardGridV2>
    </S.AdmFactorContainerV2>
  )
}

export default LzAndShotAdjustment
